import { useEffect, useState } from 'react';

import notificationService from '../firebase-notification.service';

/** How often to re-check the FCM token presence (ms). */
const NOTIFICATIONS_ENABLED_CHECK_INTERVAL_MS = 5_000;

const areNotificationsEnabled = (): boolean => notificationService.getToken() !== null;

/**
 * Reactively reports whether push notifications (FCM) are active for this
 * session — i.e. the device has a registered FCM token.
 *
 * The token lives on the notification service singleton and is populated
 * asynchronously after initialization, so a one-shot read at mount can be
 * stale. This hook re-evaluates on an interval and on window focus and stores
 * the result in state, so consumers (e.g. the polling fallback gate) react
 * once FCM finishes initializing or if it later becomes unavailable.
 */
export const useAreNotificationsEnabled = (): boolean => {
  const [enabled, setEnabled] = useState<boolean>(areNotificationsEnabled);

  useEffect(() => {
    const recheck = () => setEnabled(areNotificationsEnabled());

    recheck();
    const intervalId = setInterval(recheck, NOTIFICATIONS_ENABLED_CHECK_INTERVAL_MS);
    window.addEventListener('focus', recheck);

    return () => {
      clearInterval(intervalId);
      window.removeEventListener('focus', recheck);
    };
  }, []);

  return enabled;
};
